import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";

import {
  api,
  type CycleCreate,
  type CycleRow,
  type DeliveryGroup,
  type DeliveryItem,
  type EventCreate,
  type EventRow,
  type HarvestPatch,
  type HarvestRow,
  type IsoDate,
  type ReferenceRow,
  type StockRow,
} from "./api";

type CyclePatch = Partial<CycleCreate>;

export const qk = {
  eventsByDate: (date: IsoDate) => ["events", "date", date] as const,
  eventsByRange: (from: IsoDate, to: IsoDate) => ["events", "range", from, to] as const,
  cycles: (filter: { tray_id?: string; active?: boolean }) => ["cycles", filter] as const,
  harvests: (crop?: string) => (crop ? (["harvests", crop] as const) : (["harvests"] as const)),
  stock: () => ["stock"] as const,
  reference: () => ["reference"] as const,
  deliveries: (from: IsoDate, to: IsoDate) => ["deliveries", from, to] as const,
};

// Queries ------------------------------------------------------------

export function useEventsByDate(date: IsoDate) {
  return useQuery({
    queryKey: qk.eventsByDate(date),
    queryFn: () => api.listEventsByDate(date),
  });
}

export function useEventsByRange(from: IsoDate, to: IsoDate) {
  return useQuery({
    queryKey: qk.eventsByRange(from, to),
    queryFn: () => api.listEventsByRange(from, to),
  });
}

export function useCycles(filter: { tray_id?: string; active?: boolean } = {}) {
  return useQuery({
    queryKey: qk.cycles(filter),
    queryFn: () => api.listCycles(filter),
  });
}

export function useHarvests(crop?: string) {
  return useQuery({ queryKey: qk.harvests(crop), queryFn: () => api.listHarvests(crop) });
}

export function useStock() {
  return useQuery({ queryKey: qk.stock(), queryFn: () => api.listStock() });
}

export function useReference() {
  return useQuery({ queryKey: qk.reference(), queryFn: () => api.listReference() });
}

export function useDeliveries(from: IsoDate, to: IsoDate) {
  return useQuery({
    queryKey: qk.deliveries(from, to),
    queryFn: () => api.listDeliveries(from, to),
  });
}

// Mutations ----------------------------------------------------------

function useOptimisticEventUpdate() {
  const qc = useQueryClient();

  async function snapshotEvents(): Promise<Map<QueryKey, EventRow[] | undefined>> {
    await qc.cancelQueries({ queryKey: ["events"] });
    const snapshots = new Map<QueryKey, EventRow[] | undefined>();
    for (const [key, data] of qc.getQueriesData<EventRow[]>({ queryKey: ["events"] })) {
      snapshots.set(key, data);
    }
    return snapshots;
  }

  function restoreEvents(snapshots: Map<QueryKey, EventRow[] | undefined>) {
    for (const [key, data] of snapshots) qc.setQueryData(key, data);
  }

  function mutateEvent(eventId: string, fn: (prev: EventRow) => EventRow) {
    qc.setQueriesData<EventRow[]>({ queryKey: ["events"] }, (data) =>
      data ? data.map((e) => (e.id === eventId ? fn(e) : e)) : data,
    );
  }

  function removeEvent(eventId: string) {
    qc.setQueriesData<EventRow[]>({ queryKey: ["events"] }, (data) =>
      data ? data.filter((e) => e.id !== eventId) : data,
    );
  }

  return { qc, snapshotEvents, restoreEvents, mutateEvent, removeEvent };
}

export function useCompleteEvent() {
  const { qc, snapshotEvents, restoreEvents, mutateEvent } = useOptimisticEventUpdate();
  return useMutation<
    EventRow,
    Error,
    { id: string; completed: boolean },
    { snapshots: Map<QueryKey, EventRow[] | undefined>; wasSeed: boolean }
  >({
    mutationFn: ({ id, completed }) => api.completeEvent(id, completed),
    onMutate: async ({ id, completed }) => {
      const snapshots = await snapshotEvents();
      let wasSeed = false;
      mutateEvent(id, (prev) => {
        if (prev.kind === "seed") wasSeed = true;
        return { ...prev, completed_at: completed ? new Date().toISOString() : null };
      });
      return { snapshots, wasSeed };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) restoreEvents(ctx.snapshots);
    },
    onSettled: (data, _err, _vars, ctx) => {
      // The server path only touches stock for seed events, so only invalidate
      // the burn-down query when the toggle crossed a seed row.
      if (ctx?.wasSeed || data?.kind === "seed") {
        void qc.invalidateQueries({ queryKey: qk.stock() });
      }
    },
  });
}

export function useDeleteEvent() {
  const { snapshotEvents, restoreEvents, removeEvent } = useOptimisticEventUpdate();
  return useMutation<
    void,
    Error,
    { id: string },
    { snapshots: Map<QueryKey, EventRow[] | undefined> }
  >({
    mutationFn: ({ id }) => api.deleteEvent(id),
    onMutate: async ({ id }) => {
      const snapshots = await snapshotEvents();
      removeEvent(id);
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) restoreEvents(ctx.snapshots);
    },
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation<EventRow, Error, EventCreate>({
    mutationFn: (input) => api.createEvent(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useStartCycle() {
  const qc = useQueryClient();
  return useMutation<
    CycleRow,
    Error,
    CycleCreate,
    { snapshots: Map<QueryKey, CycleRow[] | undefined> }
  >({
    mutationFn: (input) => api.createCycle(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: ["cycles"] });
      const snapshots = new Map<QueryKey, CycleRow[] | undefined>();
      for (const [key, data] of qc.getQueriesData<CycleRow[]>({ queryKey: ["cycles"] })) {
        snapshots.set(key, data);
      }
      const tempId = `optimistic-${crypto.randomUUID()}`;
      const now = new Date().toISOString();
      const optimistic: CycleRow = {
        id: tempId,
        tray_id: input.tray_id,
        crop: input.crop,
        tray_fraction: input.tray_fraction ?? 1,
        started_at: input.started_at ?? now,
        soak_started_at: input.soak_started_at ?? null,
        seeded_at: input.seeded_at ?? null,
        uncovered_at: input.uncovered_at ?? null,
        harvested_at: null,
        target_delivery_date: input.target_delivery_date ?? null,
        plan_id: input.plan_id ?? null,
        notes: input.notes ?? null,
        phase: input.soak_started_at ? "soaking" : input.seeded_at ? "blackout" : "planned",
      };
      qc.setQueriesData<CycleRow[]>({ queryKey: ["cycles"] }, (data) =>
        data ? [optimistic, ...data] : data,
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["cycles"] });
    },
  });
}

export function useDeleteCycle() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { id: string },
    { snapshots: Map<QueryKey, CycleRow[] | undefined> }
  >({
    mutationFn: ({ id }) => api.deleteCycle(id),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: ["cycles"] });
      const snapshots = new Map<QueryKey, CycleRow[] | undefined>();
      for (const [key, data] of qc.getQueriesData<CycleRow[]>({ queryKey: ["cycles"] })) {
        snapshots.set(key, data);
      }
      qc.setQueriesData<CycleRow[]>({ queryKey: ["cycles"] }, (data) =>
        data ? data.filter((c) => c.id !== id) : data,
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["cycles"] });
    },
  });
}

export function usePatchCycle() {
  const qc = useQueryClient();
  return useMutation<
    CycleRow,
    Error,
    { id: string; patch: CyclePatch },
    { snapshots: Map<QueryKey, CycleRow[] | undefined> }
  >({
    mutationFn: ({ id, patch }) => api.patchCycle(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ["cycles"] });
      const snapshots = new Map<QueryKey, CycleRow[] | undefined>();
      for (const [key, data] of qc.getQueriesData<CycleRow[]>({ queryKey: ["cycles"] })) {
        snapshots.set(key, data);
      }
      qc.setQueriesData<CycleRow[]>({ queryKey: ["cycles"] }, (data) =>
        data ? data.map((c) => (c.id === id ? { ...c, ...patch } : c)) : data,
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["cycles"] });
    },
  });
}

export function usePatchHarvest() {
  const qc = useQueryClient();
  return useMutation<
    HarvestRow,
    Error,
    { id: string; patch: HarvestPatch },
    { snapshots: Map<QueryKey, HarvestRow[] | undefined> }
  >({
    mutationFn: ({ id, patch }) => api.patchHarvest(id, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: ["harvests"] });
      const snapshots = new Map<QueryKey, HarvestRow[] | undefined>();
      for (const [key, data] of qc.getQueriesData<HarvestRow[]>({ queryKey: ["harvests"] })) {
        snapshots.set(key, data);
      }
      qc.setQueriesData<HarvestRow[]>({ queryKey: ["harvests"] }, (data) =>
        data ? data.map((h) => (h.id === id ? { ...h, ...patch } : h)) : data,
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["harvests"] });
    },
  });
}

export function usePackHarvest() {
  const qc = useQueryClient();
  return useMutation<
    HarvestRow,
    Error,
    { id: string; packed: boolean },
    { snapshots: Map<QueryKey, DeliveryGroup[] | undefined> }
  >({
    mutationFn: ({ id, packed }) => api.packHarvest(id, packed),
    onMutate: async ({ id, packed }) => {
      await qc.cancelQueries({ queryKey: ["deliveries"] });
      const snapshots = new Map<QueryKey, DeliveryGroup[] | undefined>();
      for (const [key, data] of qc.getQueriesData<DeliveryGroup[]>({ queryKey: ["deliveries"] })) {
        snapshots.set(key, data);
      }
      const nextPackedAt = packed ? new Date().toISOString() : null;
      qc.setQueriesData<DeliveryGroup[]>({ queryKey: ["deliveries"] }, (data) =>
        data
          ? data.map((g) => ({
              ...g,
              items: g.items.map(
                (it: DeliveryItem): DeliveryItem =>
                  it.harvest_id === id ? { ...it, packed_at: nextPackedAt } : it,
              ),
            }))
          : data,
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["deliveries"] });
    },
  });
}

export function useDeleteHarvest() {
  const qc = useQueryClient();
  return useMutation<
    void,
    Error,
    { id: string },
    { snapshots: Map<QueryKey, HarvestRow[] | undefined> }
  >({
    mutationFn: ({ id }) => api.deleteHarvest(id),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: ["harvests"] });
      const snapshots = new Map<QueryKey, HarvestRow[] | undefined>();
      for (const [key, data] of qc.getQueriesData<HarvestRow[]>({ queryKey: ["harvests"] })) {
        snapshots.set(key, data);
      }
      qc.setQueriesData<HarvestRow[]>({ queryKey: ["harvests"] }, (data) =>
        data ? data.filter((h) => h.id !== id) : data,
      );
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["harvests"] });
    },
  });
}

// Re-exports for convenience
export type { CycleRow, EventRow, HarvestRow, ReferenceRow, StockRow };
